import { animated, useSpring } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import {
  TransformComponent,
  TransformWrapper,
  useTransformContext,
} from 'react-zoom-pan-pinch';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  BG_OPACITY_DIVISOR,
  DISMISS_SCALE,
  DOUBLE_CLICK_STEP,
  DRAG_SCALE_DIVISOR,
  HORIZONTAL_SWIPE_DISTANCE,
  HORIZONTAL_SWIPE_MAX_THRESHOLD,
  HORIZONTAL_SWIPE_MIN_THRESHOLD,
  HORIZONTAL_SWIPE_RATIO,
  HORIZONTAL_SWIPE_VELOCITY,
  MAX_BG_OPACITY_REDUCTION,
  MAX_DRAG_SCALE_REDUCTION,
  MAX_ZOOM_SCALE,
  SPRING_FRICTION,
  SPRING_TENSION_FAST,
  SPRING_TENSION_SLOW,
  VERTICAL_SWIPE_DISMISS_DISTANCE,
  VERTICAL_SWIPE_DISMISS_THRESHOLD,
  VERTICAL_SWIPE_DISMISS_VELOCITY,
} from './ZoomableCarousel.constants';
import styles from './ZoomableCarousel.module.css';
import type { ZoomableCarouselProps } from './ZoomableCarousel.types';

// A small helper to detect when we are at the edge of a zoomed image to allow swiping to next/prev
const ZoomStateWatcher = ({
  onZoomChange,
}: {
  onZoomChange: (isZoomed: boolean) => void;
}): null => {
  const { state } = useTransformContext();
  const isZoomed = state.scale > 1;

  useEffect(() => {
    onZoomChange(isZoomed);
  }, [isZoomed, onZoomChange]);

  return null;
};

export const ZoomableCarousel = ({
  images,
  initialIndex,
  onClose,
  reviewOverlays,
}: ZoomableCarouselProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Spring for the carousel track transition
  const [{ trackX }, trackApi] = useSpring(() => ({
    trackX: -initialIndex * window.innerWidth,
    config: { tension: SPRING_TENSION_FAST, friction: SPRING_FRICTION },
  }));

  // Spring for the backdrop background opacity
  const [{ bgOpacity }, bgApi] = useSpring(() => ({
    bgOpacity: 1,
    config: { tension: SPRING_TENSION_FAST, friction: SPRING_FRICTION },
  }));

  // Spring for vertical dismiss
  const [{ y, scale: dismissScale }, dismissApi] = useSpring(() => ({
    y: 0,
    scale: 1,
    config: { tension: SPRING_TENSION_SLOW, friction: SPRING_FRICTION },
  }));

  // Keep track of window resize
  useEffect(() => {
    const handleResize = (): void => {
      trackApi.start({
        trackX: -currentIndex * window.innerWidth,
        immediate: true,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, trackApi]);

  // Sync track position when current index changes
  useEffect(() => {
    trackApi.start({ trackX: -currentIndex * window.innerWidth });
  }, [currentIndex, trackApi]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, images.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const bind = useGesture(
    {
      onDrag: ({
        active,
        movement: [mx, my],
        velocity: [vx, vy],
        memo,
        event,
      }) => {
        // If the image is zoomed, we let react-zoom-pan-pinch handle everything
        if (isImageZoomed) return;

        // Prevent default browser drag behaviors
        if (event.cancelable) event.preventDefault();

        let dragType = memo?.dragType;
        if (!dragType) {
          dragType = Math.abs(my) > Math.abs(mx) ? 'vertical' : 'horizontal';
        }

        if (dragType === 'vertical') {
          // Vertical swipe-to-dismiss drag
          if (!active) {
            if (
              Math.abs(my) > VERTICAL_SWIPE_DISMISS_THRESHOLD ||
              (Math.abs(my) > VERTICAL_SWIPE_DISMISS_DISTANCE &&
                Math.abs(vy) > VERTICAL_SWIPE_DISMISS_VELOCITY)
            ) {
              // Animate image offscreen and close
              bgApi.start({ bgOpacity: 0 });
              dismissApi.start({
                y: my > 0 ? window.innerHeight : -window.innerHeight,
                scale: DISMISS_SCALE,
                onRest: onClose,
              });
            } else {
              // Snap back to center
              bgApi.start({ bgOpacity: 1 });
              dismissApi.start({ y: 0, scale: 1 });
            }
          } else {
            const dragScale =
              1 -
              Math.min(
                MAX_DRAG_SCALE_REDUCTION,
                Math.abs(my) / DRAG_SCALE_DIVISOR
              );
            dismissApi.start({ y: my, scale: dragScale, immediate: true });
            bgApi.start({
              bgOpacity:
                1 -
                Math.min(
                  MAX_BG_OPACITY_REDUCTION,
                  Math.abs(my) / BG_OPACITY_DIVISOR
                ),
              immediate: true,
            });
          }
        } else {
          // Horizontal swipe-between-photos
          const width = window.innerWidth;
          const swipeThreshold = Math.min(
            HORIZONTAL_SWIPE_MAX_THRESHOLD,
            Math.max(
              HORIZONTAL_SWIPE_MIN_THRESHOLD,
              width * HORIZONTAL_SWIPE_RATIO
            )
          );

          if (!active) {
            const isSwipeLeft =
              mx < -swipeThreshold ||
              (mx < -HORIZONTAL_SWIPE_DISTANCE &&
                vx > HORIZONTAL_SWIPE_VELOCITY);
            const isSwipeRight =
              mx > swipeThreshold ||
              (mx > HORIZONTAL_SWIPE_DISTANCE &&
                vx > HORIZONTAL_SWIPE_VELOCITY);

            if (isSwipeLeft && currentIndex < images.length - 1) {
              handleNext();
            } else if (isSwipeRight && currentIndex > 0) {
              handlePrev();
            } else {
              // Snap back track
              trackApi.start({ trackX: -currentIndex * width });
            }
          } else {
            trackApi.start({
              trackX: -currentIndex * width + mx,
              immediate: true,
            });
          }
        }

        return { dragType };
      },
    },
    {
      drag: {
        filterTaps: true,
        enabled: !isImageZoomed,
      },
    }
  );

  return (
    <animated.div
      ref={containerRef}
      className={styles.fullScreenOverlay}
      style={{
        backgroundColor: bgOpacity.to((o) => `rgba(0, 0, 0, ${o * 0.95})`),
      }}
    >
      {/* Top Header Row - Arrows and numbers removed as requested */}
      <div className={styles.fullScreenHeader}>
        <button
          className={styles.fullScreenCloseBtn}
          aria-label="Close Fullscreen"
          onClick={onClose}
        >
          ✕
        </button>
        <div className={styles.fullScreenProgress} />
      </div>

      {/* Main Image Container */}
      <animated.div
        className={styles.fullScreenViewport}
        {...bind()}
        style={{
          touchAction: isImageZoomed ? 'none' : 'pan-y',
          y,
          scale: dismissScale,
        }}
      >
        <animated.div
          className={styles.fullScreenTrack}
          style={{
            transform: trackX.to((val) => `translateX(${val}px)`),
            width: `${images.length * 100}%`,
          }}
        >
          {images.map((imgUrl, index) => {
            const isCurrent = index === currentIndex;
            return (
              <div key={index} className={styles.fullScreenSlide}>
                {isCurrent ? (
                  <TransformWrapper
                    initialScale={1}
                    centerOnInit={true}
                    minScale={1}
                    maxScale={MAX_ZOOM_SCALE}
                    doubleClick={{ step: DOUBLE_CLICK_STEP }}
                    wheel={{ disabled: false }}
                    panning={{ disabled: false }}
                  >
                    <ZoomStateWatcher onZoomChange={setIsImageZoomed} />
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        src={imgUrl}
                        alt={`Product view ${index + 1}`}
                        className={styles.fullScreenImage}
                        draggable={false}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                ) : (
                  <img
                    src={imgUrl}
                    alt={`Product view ${index + 1}`}
                    className={styles.fullScreenImage}
                    draggable={false}
                  />
                )}
              </div>
            );
          })}
        </animated.div>
      </animated.div>

      {/* Review Overlay */}
      {reviewOverlays && reviewOverlays[currentIndex] && (
        <div className={styles.reviewOverlayContainer}>
          <div className={styles.reviewUserRow}>
            <span className={styles.reviewUserName}>
              {reviewOverlays[currentIndex].username}
            </span>
            <span className={styles.reviewStars}>
              {reviewOverlays[currentIndex].rating} ★
            </span>
          </div>
          <p className={styles.reviewText}>
            {reviewOverlays[currentIndex].text}
          </p>
        </div>
      )}
    </animated.div>
  );
};

export default ZoomableCarousel;
